import React, { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import useTenantTranslation from 'utils/hooks/useTenantTranslation';
import useTenantColors from 'utils/hooks/useTenantColors';
import { useAppDispatch, useAppSelector } from 'store/configureStore';
import {
  connectToCRFProduct,
  fetchCRFLoanProducts,
} from 'store/actions/funding';
import {
  connectToCRFProductErrorSelector,
  crfLoansConnectedCRFProductSelector,
  crfLoansIsConnectingToCRFProduct,
} from 'store/selectors/funding';
import Loader from 'app/components/Loader';
import { crfProductConnectModalSelector } from 'store/selectors/modal';
import './styles.scss';
import { toggleCRFProductConnectModal } from 'store/reducers/modal';
import bellIcon from 'assets/img/funding/crf-connect/bell-icon.svg';
import phoneIcon from 'assets/img/funding/crf-connect/phone-icon.svg';
import { CgArrowRightO } from 'react-icons/cg';
import { resetCRFConnectedProductState } from 'store/reducers/funding';
import { useMediaQuery } from 'react-responsive';
import { phoneConstant } from '_constants/otherConstants';
import ErrorsText from 'app/components/ErrorsText';
import BaseModal from '../BaseModal';

const classNames = {
  modal: 'crf_connect_modal',
  closeIcon: 'crf_connect_modal__closeIcon',
  declineButton: 'crf_connect_modal__declineButton',
  acceptButton: 'crf_connect_modal__acceptButton',
  modalFooter: 'crf_connect_modal__modalFooter',
  modalInnerContainer: 'crf_connect_modal__modalInnerContainer',
};

const LenderContactInformation: React.FC<{
  contactName: string;
  contactTitle?: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  contactAddress?: string;
}> = ({
  contactName,
  contactAddress,
  contactPhone,
  website,
  contactEmail,
  contactTitle,
}) => {
  const { t } = useTenantTranslation();
  return (
    <div>
      <h3 className="crf_connect_modal__title">
        {t('funding_module.crf_connect.connect_modal.lender_info_title')}
      </h3>
      <p className="crf_connect_modal__subTitle">
        {t('funding_module.crf_connect.connect_modal.lender_info_sub_title')}
      </p>
      <div className="crf_connect_modal__lenderContactInfoList">
        <p>
          <span>
            {t('funding_module.crf_connect.connect_modal.lender_name_label')}:
          </span>{' '}
          {contactName}
          {!!contactTitle && `, ${contactTitle}`}
        </p>
        {contactEmail && (
          <p>
            <span>
              {t('funding_module.crf_connect.connect_modal.lender_email_label')}
              :
            </span>{' '}
            {contactEmail}
          </p>
        )}

        {contactPhone && (
          <p>
            <span>
              {t('funding_module.crf_connect.connect_modal.lender_phone_label')}
              :
            </span>{' '}
            {contactPhone}
          </p>
        )}

        {website && (
          <p className="crf_connect_modal__lenderContactInfoList__website">
            <span>
              {t(
                'funding_module.crf_connect.connect_modal.lender_website_label',
              )}
              :
            </span>{' '}
            <a href={website} target="_blank" rel="noreferrer">
              {website}
            </a>
          </p>
        )}

        {contactAddress && (
          <p>
            <span>
              {t(
                'funding_module.crf_connect.connect_modal.lender_address_label',
              )}
              :
            </span>{' '}
            {contactAddress}
          </p>
        )}
      </div>
    </div>
  );
};

const FirstConnectionView: React.FC<{ productName: string }> = ({
  productName,
}) => {
  const { t } = useTenantTranslation();
  return (
    <div>
      <h3 className="crf_connect_modal__title">
        <Trans components={[<p key="0" />]}>
          {t(
            'funding_module.crf_connect.connect_modal.first_connection_title',
            {
              productName,
            },
          )}
        </Trans>
      </h3>
      <p className="crf_connect_modal__subTitle">
        {t(
          'funding_module.crf_connect.connect_modal.first_connection_sub_title',
        )}
      </p>

      <div className="crf_connect_modal__productFirstTimeConnectionView">
        <div className="crf_connect_modal__productFirstTimeConnectionView__Step">
          <img src={bellIcon} alt="bell" />
          <div>
            <h4>
              {t('funding_module.crf_connect.connect_modal.step_one_title')}
            </h4>
            <p>
              {t(
                'funding_module.crf_connect.connect_modal.step_one_description',
              )}
            </p>
          </div>
        </div>

        <div className="crf_connect_modal__productFirstTimeConnectionView__Step">
          <img src={phoneIcon} alt="bell" />
          <div>
            <h4>
              {t('funding_module.crf_connect.connect_modal.step_two_title')}
            </h4>
            <p>
              {t(
                'funding_module.crf_connect.connect_modal.step_two_description',
              )}
            </p>
          </div>
        </div>

        <div className="crf_connect_modal__productFirstTimeConnectionView__Step">
          <img src={bellIcon} alt="bell" />
          <div>
            <h4>
              {t('funding_module.crf_connect.connect_modal.step_three_title')}
            </h4>
            <p>
              {t(
                'funding_module.crf_connect.connect_modal.step_three_description',
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CRFProductConnectModal: React.FC = () => {
  const isPhone = useMediaQuery({ query: `(max-width: ${phoneConstant}px)` });
  const { t } = useTenantTranslation();
  const [showLenderInfo, setShowLenderInfo] = useState(false);
  const { colorVariables } = useTenantColors();

  const isConnectingToCRFProduct = useAppSelector(
    crfLoansIsConnectingToCRFProduct,
  );
  const crfLoansConnectedCRFProduct = useAppSelector(
    crfLoansConnectedCRFProductSelector,
  );
  const connectToCRFProductError = useAppSelector(
    connectToCRFProductErrorSelector,
  );

  const crfProductConnectModal = useAppSelector(crfProductConnectModalSelector);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (crfProductConnectModal?.productId) {
      dispatch(
        connectToCRFProduct({ productId: crfProductConnectModal?.productId }),
      );
    }
  }, [crfProductConnectModal?.productId]);

  useEffect(() => {
    if (crfLoansConnectedCRFProduct?.isNewLead === false) {
      setShowLenderInfo(true);
    }
  }, [crfLoansConnectedCRFProduct]);

  useEffect(() => {
    /** Update list of products */
    if (crfLoansConnectedCRFProduct?.isNewLead === true) {
      dispatch(fetchCRFLoanProducts());
    }
  }, [crfLoansConnectedCRFProduct]);

  const handleModalClose = () => {
    dispatch(resetCRFConnectedProductState());
    dispatch(toggleCRFProductConnectModal(null));
  };

  const declineButtonProps = useMemo(
    () => ({
      withDeclineButton: !crfLoansConnectedCRFProduct?.isNewLead,
      declineButtonText: crfLoansConnectedCRFProduct?.isNewLead
        ? undefined
        : 'Close',
    }),
    [crfLoansConnectedCRFProduct?.isNewLead],
  );

  const acceptButtonProps = useMemo(
    () => ({
      withAcceptButton: crfLoansConnectedCRFProduct?.isNewLead,
      acceptButtonText: showLenderInfo
        ? crfLoansConnectedCRFProduct?.isNewLead
          ? t(
              'funding_module.crf_connect.connect_modal.first_connection_complete_btn',
            )
          : undefined
        : t(
            'funding_module.crf_connect.connect_modal.first_connection_next_btn',
          ),
      onAcceptButtonClick: () => {
        if (showLenderInfo) {
          handleModalClose();
        } else {
          setShowLenderInfo(true);
        }
      },
      acceptButtonRightIcon: (
        <CgArrowRightO
          size={24}
          color={colorVariables?.icons.icon_primary1_button}
        />
      ),
    }),
    [crfLoansConnectedCRFProduct?.isNewLead, showLenderInfo],
  );

  const renderDetals = () => {
    if (connectToCRFProductError) {
      return (
        <ErrorsText
          errorsListOrErrorText={t(
            'funding_module.crf_connect.connect_modal.failed_to_connect',
          )}
        />
      );
    }

    if (showLenderInfo && crfLoansConnectedCRFProduct) {
      return (
        <LenderContactInformation
          contactName={crfLoansConnectedCRFProduct.leadDetails.contactName}
          contactTitle={crfLoansConnectedCRFProduct.leadDetails.contactTitle}
          contactEmail={crfLoansConnectedCRFProduct.leadDetails.contactEmail}
          contactPhone={crfLoansConnectedCRFProduct.leadDetails.contactPhone}
          website={crfLoansConnectedCRFProduct.leadDetails.website}
          contactAddress={
            crfLoansConnectedCRFProduct.leadDetails.contactAddress
          }
        />
      );
    }

    if (!showLenderInfo && crfLoansConnectedCRFProduct?.isNewLead) {
      return (
        <FirstConnectionView
          productName={crfLoansConnectedCRFProduct.leadDetails.details.name!}
        />
      );
    }

    return null;
  };

  return (
    // @ts-ignore
    <BaseModal
      onModalClose={handleModalClose}
      // @ts-ignore
      closeButtonSize={isPhone ? 'medium' : 'large'}
      declineButtonSize="large"
      withDeclineButton={
        isConnectingToCRFProduct ? false : declineButtonProps.withDeclineButton
      }
      declineButtonText={declineButtonProps.declineButtonText}
      declineButtonLook="filled-gray"
      withAcceptButton={
        isConnectingToCRFProduct ? false : acceptButtonProps.withAcceptButton
      }
      acceptButtonText={acceptButtonProps.acceptButtonText}
      acceptButtonSize="large"
      acceptButtonRightIcon={acceptButtonProps.acceptButtonRightIcon}
      onAcceptButtonClick={acceptButtonProps.onAcceptButtonClick}
      onDeclineButtonClick={handleModalClose}
      classNames={classNames}
      isCloseIconVisible
    >
      {isConnectingToCRFProduct ? <Loader /> : <>{renderDetals()}</>}
    </BaseModal>
  );
};
